import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaHome,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaUserCheck,
  FaHourglassHalf,
} from "react-icons/fa";
import {
  getMySchedules,
  subscribeSchedules,
  unsubscribeSchedules,
} from "../services/scheduleService";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../styles/user/HistoryAppointment.css";

export default function HistoryAppointment() {
  const [schedules, setSchedules] = useState([]);
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const PER_PAGE = 6;

  const statusOrder = {
    pending: 1,
    assigned: 2,
    accepted: 3,
    viewed: 4,
    done: 5,
    canceled: 6,
  };

  // Load initial data
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const mySchedules = await getMySchedules();
        setSchedules(mySchedules);
      } catch (err) {
        console.log("❌ Lỗi fetch schedules:", err);
      }
    };
    fetchSchedules();
  }, []);

  // Realtime updates
  useEffect(() => {
    subscribeSchedules(
      (newSchedule) => {
        setSchedules((prev) => {
          const exists = prev.some((s) => s._id === newSchedule._id);
          if (exists) return prev;
          return [newSchedule, ...prev];
        });
      },
      (updated) => {
        setSchedules((prev) =>
          prev
            .map((s) => (s._id === updated._id ? updated : s))
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        );
      },
      (deletedId) => {
        setSchedules((prev) => prev.filter((s) => s._id !== deletedId));
      }
    );
    return () => unsubscribeSchedules();
  }, []);

  // Responsive
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Sort by status
  const schs = [...schedules].sort((a, b) => {
    const orderA = statusOrder[a.status] || 99;
    const orderB = statusOrder[b.status] || 99;
    if (orderA !== orderB) return orderA - orderB;
    return new Date(b.updatedAt) - new Date(a.updatedAt);
  });

  // Pagination
  const paginate = (arr, page, perPage) => {
    const start = (page - 1) * perPage;
    return arr.slice(start, start + perPage);
  };
  const totalPages = Math.max(1, Math.ceil(schs.length / PER_PAGE));
  const items = paginate(schs, page, PER_PAGE);

  return (
    <div className="history-page">
      <Header />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="history-container"
      >
        <h2 className="history-title">
          <FaClock className="history-icon" /> Lịch sử đặt phòng
        </h2>

        {schs.length === 0 ? (
          <p className="empty">Bạn chưa có lịch đặt phòng nào.</p>
        ) : isMobile ? (
          // === Mobile view: CARD ===
          <motion.div
            className="history-card-list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {items.map((sch, i) => (
              <motion.div
                key={sch._id}
                className="history-card"
                whileHover={{ scale: 1.02 }}
              >
                <div className="card-header">
                  <FaHome className="room-icon" />
                  <span className="room-name">
                    {sch.room_id?.apartmentName || "Phòng không rõ"}
                  </span>
                </div>

                <div className="card-body">
                  <p>
                    <strong>Bắt đầu:</strong>{" "}
                    {sch.start_time
                      ? new Date(sch.start_time).toLocaleString("vi-VN")
                      : "—"}
                  </p>
                  <p>
                    <strong>Kết thúc:</strong>{" "}
                    {sch.end_time
                      ? new Date(sch.end_time).toLocaleString("vi-VN")
                      : "—"}
                  </p>
                  <p>
                    <strong>Trạng thái:</strong> {renderStatus(sch.status)}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          // === Desktop view: TABLE ===
          <div className="table-wrapper">
            <motion.table
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="history-table"
            >
              <thead>
                <tr>
                  <th>STT</th>
                  <th>Tên phòng</th>
                  <th>Thời gian bắt đầu</th>
                  <th>Thời gian kết thúc</th>
                  <th>Trạng thái</th>
                </tr>
              </thead>

              <AnimatePresence>
                <tbody>
                  {items.map((sch, i) => (
                    <motion.tr
                      key={sch._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      whileHover={{
                        backgroundColor: "#fff8f3",
                        scale: 1.01,
                      }}
                    >
                      <td>{(page - 1) * PER_PAGE + i + 1}</td>
                      <td>
                        <FaHome className="table-icon" />{" "}
                        {sch.room_id?.apartmentName || "Phòng không rõ"}
                      </td>
                      <td>
                        {sch.start_time
                          ? new Date(sch.start_time).toLocaleString("vi-VN")
                          : "—"}
                      </td>
                      <td>
                        {sch.end_time
                          ? new Date(sch.end_time).toLocaleString("vi-VN")
                          : "—"}
                      </td>
                      <td>{renderStatus(sch.status)}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </AnimatePresence>
            </motion.table>
          </div>
        )}

        {schs.length > PER_PAGE && (
          <Pagination current={page} total={totalPages} onChange={setPage} />
        )}
      </motion.div>

      <Footer />
    </div>
  );
}

// ===== Render trạng thái =====
function renderStatus(status) {
  const base = "status-tag";
  switch (status) {
    case "done":
      return (
        <span className={`${base} green`}>
          <FaCheckCircle /> Hoàn tất
        </span>
      );
    case "canceled":
      return (
        <span className={`${base} red`}>
          <FaTimesCircle /> Đã hủy
        </span>
      );
    case "viewed":
      return (
        <span className={`${base} blue`}>
          <FaEye /> Đã xem
        </span>
      );
    case "accepted":
      return (
        <span className={`${base} teal`}>
          <FaUserCheck /> Đã chấp nhận
        </span>
      );
    case "assigned":
      return (
        <span className={`${base} indigo`}>
          <FaUserCheck /> Đang chờ
        </span>
      );
    case "pending":
      return (
        <span className={`${base} yellow`}>
          <FaHourglassHalf /> Chờ xử lý
        </span>
      );
    default:
      return <span className={`${base} gray`}>Không rõ</span>;
  }
}

// ===== Pagination =====
function Pagination({ current, total, onChange }) {
  const pages = Array.from({ length: total }, (_, i) => i + 1);
  return (
    <div className="pagination">
      <button
        className="page-btn"
        disabled={current === 1}
        onClick={() => onChange(current - 1)}
      >
        ‹
      </button>
      {pages.map((p) => (
        <button
          key={p}
          className={`page-btn ${p === current ? "active" : ""}`}
          onClick={() => onChange(p)}
        >
          {p}
        </button>
      ))}
      <button
        className="page-btn"
        disabled={current === total}
        onClick={() => onChange(current + 1)}
      >
        ›
      </button>
    </div>
  );
}
